import { useState } from "react";
import { format, parseISO } from "date-fns";
import { X } from "lucide-react";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Label } from "@/modern/components/ui/label";
import { Textarea } from "@/modern/components/ui/textarea";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import type { TimeOffDetail } from "../types";
import { TIME_OFF_APPROVAL, TIME_OFF_APPROVAL_LABEL } from "../status/displayStatusColors";

function formatRange(start: string, end: string): string {
  try {
    return `${format(parseISO(start), "MMM d, yyyy")} → ${format(parseISO(end), "MMM d, yyyy")}`;
  } catch {
    return `${start} → ${end}`;
  }
}

type TimeOffDetailPanelProps = {
  detail: TimeOffDetail;
  canManage?: boolean;
  isActing?: boolean;
  onClose: () => void;
  onApprove: (adminNotes?: string) => Promise<void>;
  onDeny: (adminNotes?: string) => Promise<void>;
};

export function TimeOffDetailPanel({
  detail,
  canManage = false,
  isActing,
  onClose,
  onApprove,
  onDeny,
}: TimeOffDetailPanelProps) {
  const [adminNotes, setAdminNotes] = useState(detail.admin_notes || "");
  const pending = detail.approval_status === TIME_OFF_APPROVAL.PENDING;
  const showActions = canManage && pending;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      <ScrollArea className="flex-1">
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0 space-y-2">
            <div>
              <p className="truncate text-base font-semibold leading-tight text-foreground">
                {detail.resource_name}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">Time off request</p>
            </div>
            <Badge variant="muted" size="sm">
              {TIME_OFF_APPROVAL_LABEL[detail.approval_status] ?? "Unknown"}
            </Badge>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="size-7 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={onClose}
            aria-label="Close panel"
          >
            <X className="size-3.5" />
          </Button>
        </div>

        <div className="space-y-6 px-5 py-5 text-sm">
          <div className="space-y-1">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Date range
            </p>
            <p>{formatRange(detail.start_at, detail.end_at)}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Reason
            </p>
            <p>{detail.reason || "—"}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="time-off-admin-notes" className="text-sm font-medium">
              Admin notes
            </Label>
            <Textarea
              id="time-off-admin-notes"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={3}
              disabled={!showActions}
            />
          </div>
        </div>
      </ScrollArea>

      {showActions ? (
        <div className="flex gap-2 border-t border-border bg-background px-5 py-3">
          <Button
            size="sm"
            disabled={isActing}
            onClick={() => void onApprove(adminNotes.trim() || undefined)}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={isActing}
            onClick={() => void onDeny(adminNotes.trim() || undefined)}
          >
            Deny
          </Button>
        </div>
      ) : null}
    </div>
  );
}
